import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  viewChild,
  ViewChild
} from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import { RegionItemDto, RegionService, SortDto, } from '@artemis-software/wr-api';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  exhaustMap,
  Observable,
  of,
  startWith,
  Subject,
  tap,
} from 'rxjs';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { NbPopoverDirective } from '@nebular/theme';
import OrderEnum = SortDto.OrderEnum;

@Component({
  standalone: false,
  selector: 'wr-region-select',
  templateUrl: './region-select.component.html',
  styleUrls: ['./region-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RegionSelectComponent),
      multi: true,
    },
  ],
})
export class RegionSelectComponent extends AbstractFormControl<RegionItemDto> implements OnInit {

  readonly regionService = inject(RegionService);
  readonly router = inject(Router);

  popover = viewChild(NbPopoverDirective);

  @ViewChild('searchInput')
  input?: ElementRef;

  @Input()
  tenantId$?: Observable<string>;

  @Input()
  fixedTenantId: string | undefined = undefined;

  @Output() regionSelected = new EventEmitter<RegionItemDto>();

  filterText$ = new BehaviorSubject<string>('');

  lastTenantId: string | undefined = undefined;

  nextPage = 0;

  trigger$ = new Subject<'reset' | 'filter' | 'loadMore'>();

  regions$ = new BehaviorSubject<RegionItemDto[]>([]);

  ngOnInit(): void {
    combineLatest([
      this.filterText$.pipe(debounceTime(100)),
      this.tenantId$ ? this.tenantId$.pipe(startWith(undefined)) : of(undefined),
      this.trigger$,
    ])
      .pipe(
        exhaustMap(([filterText, tenantId, trigger]) => {
          let appliedFilterText: string | undefined = undefined;
          if (filterText?.length > 0) {
            appliedFilterText = filterText;
          }

          const filterTenantId = this.fixedTenantId || tenantId;

          if (this.lastTenantId !== filterTenantId) {
            trigger = 'reset';
            this.lastTenantId = filterTenantId;
          }
          if (trigger === 'reset') {
            this.nextPage = 0;
          }

          return this.regionService.findAll({
            tenantId: filterTenantId,
            text: appliedFilterText,
            page: this.nextPage,
            size: 10,
            sort: {
              sort: 'name',
              order: OrderEnum.Asc,
            },
          }).pipe(
            tap(regions => {
              if (trigger === 'reset') {
                this.regions$.next(regions);
              } else {
                const current = this.regions$.value || [];
                this.regions$.next([...current, ...regions]);
              }
              this.nextPage++;
            }),
          );
        })
      )
      .subscribe();
  }

  trackById(index: number, item: RegionItemDto): string {
    return item.id;
  }

  override writeValue(region: RegionItemDto): void {
    super.writeValue(region);
    this.filterText$.next(this.viewHandle(region));
  }

  onSelectionChange(region?: RegionItemDto): void {
    if (region?.id) {
      this.filterText$.next(this.viewHandle(region));
      this.value = region;
      this.regionSelected.emit(region);
      this.popover()?.hide();
    }
  }

  viewHandle(value?: RegionItemDto | string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value) {
      return value.name + ' (' + value.tenant.name + ')';
    }
    return '';
  }

  clear(): void {
    this.value = undefined;
    this.filterText$.next('');
    this.trigger$.next('reset');
  }

  goToRegion(): void {
    this.router.navigate(['region', this.value?.id]);
  }

  getRegionTitle(region: RegionItemDto | undefined): string {
    if (!region) {
      return '';
    }
    return region.name + ' (' + region.tenant.name + ')';
  }
}
